import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { normalizeFirestoreDate } from "../../../lib/firestoreDate";

/**
 * Fetch reportHealth docs for a client's visible calendar **month**.
 *
 * IMPORTANT: Documents are keyed by calendar day (`dateIndex`, usually same as Firestore doc id
 * `YYYY-MM-DD`). We query by document id range so rows appear even when the client synced on a later
 * day (mobile `createdOn` can fall outside the month).
 *
 * Path: organizations/{organizationId}/clients/{clientId}/reportHealth/{YYYY-MM-DD}
 */
export function useClientReportHealth({ clientId, organizationId, start, end }) {
  const [healthReports, setHealthReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);

  const isValid = Boolean(clientId && organizationId && start && end);
  const rangeKey =
    start instanceof Date && end instanceof Date
      ? `${format(start, "yyyy-MM-dd")}..${format(end, "yyyy-MM-dd")}`
      : null;

  useEffect(() => {
    if (!isValid || !rangeKey) {
      setHealthReports([]);
      setLoading(true); // Keep loading until we have valid params (e.g. auth ready)
      setError(null);
      return;
    }
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
    setLoading(true);
    setError(null);

    const idxStart = format(start, "yyyy-MM-dd");
    const idxEnd = format(end, "yyyy-MM-dd");

    /** @type {typeof firebase.firestore.FieldPath} */
    const FieldPath = firebase.firestore.FieldPath;

    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("clients")
      .doc(clientId)
      .collection("reportHealth")
      .orderBy(FieldPath.documentId())
      .startAt(idxStart)
      .endAt(idxEnd);

    unsubscribeRef.current = ref.onSnapshot(
      (snapshot) => {
        const list = snapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            ...d,
            dateIndex: typeof d.dateIndex === "string" ? d.dateIndex : doc.id,
            createdOn: normalizeFirestoreDate(d.createdOn),
            updatedOn: normalizeFirestoreDate(d.updatedOn),
          };
        }).sort((a, b) => {
          /** Newest logical day last in snapshot order from startAt/endAt ascend — prefer latest updated */
          const idA = a.id ?? "";
          const idB = b.id ?? "";
          return idB.localeCompare(idA);
        });
        setHealthReports(list);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setHealthReports([]);
        setLoading(false);
      }
    );
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [clientId, organizationId, start, end, isValid, rangeKey]);

  return { healthReports, loading, error };
}
